import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfigProvider } from '@app/app.config.provider';
import { SUBPRODUCTS_SEPARATOR } from '@components/settings/product-setup/product-setup-spread';
import { IProduct, IProductGroup, IProductTab, IProductTag, sortProductGroups, sortProducts, TAGS } from '@models/product';
import { StoreActions } from '@models/store/store-actions';
import { IStoreState } from '@models/store/store-state';
import { VintageService } from '@services/decay/vintage.service';
import { clone } from '@shared/arrays';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ObservableStore } from '../observable-store/dist';

@Injectable({ providedIn: 'root' })
export class ProductService extends ObservableStore<IStoreState> {
  private apiProducts: string;
  private apiProductGroups: string;
  private apiProductTag: string;
  private apiProductTab: string;

  constructor(
    private readonly http: HttpClient,
    private readonly vintageService: VintageService,
    private readonly appConfig: AppConfigProvider
  ) {
    super({ trackStateHistory: false });

    this.appConfig.environment.subscribe(env => {
      this.apiProducts = `${env.API.REFERENCE_DATA}/api/products`;
      this.apiProductGroups = `${env.API.REFERENCE_DATA}/api/productgroups`;
      this.apiProductTag = `${env.API.REFERENCE_DATA}/api/producttag`;
      this.apiProductTab = `${env.API.REFERENCE_DATA}/api/producttab`;
    });
  }

  public getProduct(productId: number): Observable<IProduct> {
    return this.getProducts().pipe(
      map(products => {
        const product = products.find(({ ProductId }) => ProductId === productId);

        if (product && !product.Vintages) {
          this.vintageService.getVintages(product.ProductId).subscribe();
        }

        this.setState({ product }, StoreActions.GetProduct);
        return product;
      })
    );
  }

  public getProducts(): Observable<IProduct[]> {
    const state = this.getState();

    if (state && state.products && state.products.length > 1 && state.productGroups && state.productGroups.length > 0) {
      return of(state.products);
    } else {
      return this.fetchProducts();
    }
  }

  public fetchProducts(): Observable<IProduct[]> {
    return forkJoin([this.http.get<IProduct[]>(this.apiProducts), this.getProductGroups(), this.fetchProductTags()]).pipe(
      map(([products, productGroups, productTags]) => {
        if (!products) {
          products = [];
        }
        sortProducts(products, productGroups);
        if (productTags) {
          const counterparties = productTags.filter(tag => tag.Group === 'CounterpartyType');
          const productTypes = productTags.filter(tag => tag.Group === 'ProductType');
          for (const product of products) {
            const counterParty = counterparties.find(cp => cp.Key === product.CounterpartyType);
            product.CounterpartyText = counterParty ? counterParty.Value : '';
            const productType = productTypes.find(pt => pt.Key === product.ProductType);
            product.ProductTypeText = productType ? productType.Value : '';
          }
        }

        this.setState({ products }, StoreActions.SetProducts);
        return products;
      }),
      catchError(err => {
        return this.handleError(err);
      })
    );
  }

  public getProductGroups(): Observable<IProductGroup[]> {
    const state = this.getState();
    if (state && state.productGroups && state.productGroups.length > 1) {
      return of(state.productGroups);
    } else {
      return this.fetchProductGroups();
    }
  }

  public fetchProductGroups(): Observable<IProductGroup[]> {
    return this.http.get<IProductGroup[]>(this.apiProductGroups).pipe(
      map((productGroups: IProductGroup[]) => {
        if (!productGroups) {
          productGroups = [];
        }
        sortProductGroups(productGroups);
        this.setState({ productGroups }, StoreActions.SetProductGroups);
        return productGroups;
      }),
      catchError(err => {
        return this.handleError(err);
      })
    );
  }

  public getProductTypes(): Observable<IProductTag[]> {
    const state = this.getState();
    if (state && state.productTypes && state.productTypes.length > 1) {
      return of(state.productTypes);
    } else {
      return this.fetchProductTags('productTypes');
    }
  }

  public getCounterpartyTypes(): Observable<IProductTag[]> {
    const state = this.getState();
    if (state && state.counterpartyTypes && state.counterpartyTypes.length > 1) {
      return of(state.counterpartyTypes);
    } else {
      return this.fetchProductTags('counterpartyTypes');
    }
  }

  public fetchProductTags(returnType: 'productTypes' | 'counterpartyTypes' = null): Observable<IProductTag[]> {
    return this.http.get<IProductTag[]>(this.apiProductTag).pipe(
      map((productTags: IProductTag[]) => {
        const productTypes = [];
        const counterparties = [];

        productTags.forEach(tag => {
          switch (tag.Group) {
            case 'ProductType':
              productTypes.push(tag);
              break;
            case 'CounterpartyType':
              counterparties.push(tag);
          }
        });

        this.setState({ productTags }, StoreActions.SetProductTags);

        switch (returnType) {
          case 'productTypes':
            return productTypes;
          case 'counterpartyTypes':
            return counterparties;
          default:
            return productTags;
        }
      }),
      catchError(err => {
        return this.handleError(err);
      })
    );
  }

  public getProductTabs(): Promise<IProductTab[]> {
    return forkJoin([this.http.get<IProductTab[]>(this.apiProductTab), this.getProductGroups(), this.fetchProductTags()])
      .pipe(
        map(res => {
          const [productTabs, productGroups, productTags] = res;

          // Sorting The list of tabs
          productTabs.sort((a, b) => {
            if (a.IsPublished || b.IsPublished) {
              return Number(b.IsPublished) - Number(a.IsPublished);
            } else {
              return a.Name.localeCompare(b.Name);
              // return new Date(a.DateTimeCreated).getTime() - new Date(b.DateTimeCreated).getTime();
            }
          });

          // Sorting list of products
          productTabs.forEach(tab => {
            sortProducts(tab.Products, productGroups);
          });

          // Adding Additional Fields to products i.e. counter party text, ProductTypeText
          productTabs.forEach(tab => {
            tab.Products.forEach(product => {
              const counterparties = productTags.filter(tag => tag.Group === 'CounterpartyType');
              const productTypes = productTags.filter(tag => tag.Group === 'ProductType');

              const counterParty = counterparties.find(cp => cp.Key === product.CounterpartyType);
              product.CounterpartyText = counterParty ? counterParty.Value : '';
              const productType = productTypes.find(pt => pt.Key === product.ProductType);
              product.ProductTypeText = productType ? productType.Value : '';
              if (product.SubProducts && product.SubProducts.length > 0) {
                product.SubProducts = product.SubProducts.replace('&amp;', '&')
                  .replace('&gt;', '>')
                  .replace('&lt;', '<');

                // NULL Tag: replace 'NULLSUBPRODUCT' with '<null>'
                const nullSubproductBackend = new RegExp('NULLSUBPRODUCT', 'ig');
                product.SubProducts = product.SubProducts.replace(nullSubproductBackend, TAGS.NULL_TAG);
              }

              // Default Tag: Add '<default>' string to subproducts if needed.
              if (product.IsDefaultProduct) {
                product.SubProducts = [TAGS.DEFAULT_TAG, ...(product.SubProducts || '').split(SUBPRODUCTS_SEPARATOR).map(p => p.trim())]
                  .filter(s => s)
                  .join(SUBPRODUCTS_SEPARATOR);
              }
            });
          });
          return productTabs;
        }),
        // Adding Additional Fields to tabs i.e. DB version
        map(tabs => {
          tabs.forEach(x => (x.ProductsDB = clone(x.Products)));
          return tabs;
        })
      )
      .toPromise();
  }

  private handleError(error: any) {
    console.error('Server error:', error);
    if (error.error instanceof Error) {
      const errMessage = error.error.message;
      return throwError(errMessage);
    }
    return throwError(error || 'Server error');
  }
}
